// frontend/src/pages/Quiz.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, AlertCircle, ExternalLink } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

interface QuizInfo {
  id: number;
  google_form_url: string;
  seuil_reussite: number;
}

interface QuizResult {
  promotion_id: number;
  score_percentage: number;
  passed: boolean;
  taken_at: string;
}

export default function Quiz() {
  const { promotionId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<QuizInfo | null>(null);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  

  useEffect(() => {
    fetchQuizInfo();
  }, [promotionId]);

  const fetchQuizInfo = async () => {
    try {
      // Récupérer les infos du quiz
      const quizRes = await api.get(`/quiz/promotion/${promotionId}`);
      setQuiz(quizRes.data);
      
      // Récupérer les résultats précédents
      const resultsRes = await api.get('/quiz/results', {
        params: { promotion_id: promotionId }
      });
      setResults(resultsRes.data);
    } catch (error: any) {
      if (error.response?.status === 400) {
        toast.error('Vous devez terminer tous les modules avant de passer le quiz');
        navigate(`/promotions/${promotionId}/modules`);
      } else {
        toast.error('Erreur lors du chargement du quiz');
      }
    } finally {
      setLoading(false);
    }
  };

  const bestScore = results.length > 0 
    ? Math.max(...results.map(r => r.score_percentage))
    : null;

  const hasPassed = results.some(r => r.passed);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!quiz) {
    return <div>Quiz non trouvé</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Quiz de validation</h1>
        <p className="mt-2 text-gray-600">
          Testez vos connaissances pour valider votre formation
        </p>
      </div>

      {/* Informations sur le quiz */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Informations importantes</h2>
        <div className="space-y-3">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
            <div>
              <p className="font-medium">Seuil de réussite: {quiz.seuil_reussite}%</p>
              <p className="text-sm text-gray-600">
                Vous devez obtenir au moins {quiz.seuil_reussite}% pour valider le quiz
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <Trophy className="h-5 w-5 text-primary mr-2 mt-0.5" />
            <div>
              <p className="font-medium">Nombre d'essais illimité</p>
              <p className="text-sm text-gray-600">
                Vous pouvez repasser le quiz autant de fois que nécessaire
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <ExternalLink className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
            <div>
              <p className="font-medium">Important: Utilisez votre email de connexion</p>
              <p className="text-sm text-gray-600">
                Assurez-vous d'utiliser le même email que celui de votre compte pour que votre score soit enregistré
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Historique des résultats */}
      {results.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Vos tentatives précédentes</h2>
          <div className="space-y-3">
            {results.map((result, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  result.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center">
                  <Trophy className={`h-5 w-5 mr-3 ${
                    result.passed ? 'text-green-600' : 'text-red-600'
                  }`} />
                  <div>
                    <p className="font-medium">
                      Score: {result.score_percentage}%
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(result.taken_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  result.passed 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.passed ? 'Réussi' : 'Échoué'}
                </span>
              </div>
            ))}
          </div>
          
          {bestScore !== null && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">
                Votre meilleur score: {bestScore}%
                {hasPassed && ' ✅ Quiz validé'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Bouton pour lancer le quiz */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          {!showQuiz ? (
            <>
              <p className="mb-6 text-gray-600">
                Prêt à tester vos connaissances ?
              </p>
              <button
                onClick={() => setShowQuiz(true)}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Trophy className="h-5 w-5 mr-2" />
                {results.length > 0 ? 'Repasser le quiz' : 'Commencer le quiz'}
              </button>
            </>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Le quiz s'ouvre ci-dessous. N'oubliez pas d'utiliser votre email de connexion !
                </p>
              </div>
              <iframe
                src={quiz.google_form_url}
                width="100%"
                height="800"
                frameBorder="0"
                marginHeight={0}
                marginWidth={0}
                className="rounded-lg border"
              >
                Chargement...
              </iframe>
            </>
          )}
        </div>
      </div>
    </div>
  );
}